import React, { useState, useCallback, useEffect, FC } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { loadMorePosts, refreshPosts, toggleUpvote, toggleDownvote, toggleReaction } from '../../reducers/postReducer';
import { notify } from '../../reducers/notificationReducer';
import InfiniteScrollSentinel from '../InfiniteScrollSentinel';
import EndOfFeed from '../EndOfFeed';
import EmptyState from '../EmptyState';
import GoatMascot from '../GoatMascot';
import LoadingSpinner from '../LoadingSpinner';
import PostCard from '../PostCard';
import PostCardSkeleton from '../PostCardSkeleton';
import getErrorMsg from '../../utils/getErrorMsg';
import { trackScrollDepth } from '../../utils/posthog';
import { usePullToRefresh } from '../../utils/usePullToRefresh';
import { useScrollRestoration } from '../../utils/useScrollRestoration';

import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  Button,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { handleJoinHerd, isJoined } from '../../utils/JoinHerdToggle';
import { useMobileHeader } from './MobileHeaderContext';
import { useAuthSheet } from './AuthSheetContext';
import SwipeTabViews from './SwipeTabViews';

const MobilePostListCompact: FC = () => {
  const posts = useAppSelector((state) => state.posts as any);
  // The feed data survives navigation — derive the pagination cursor from it so
  // a restored multi-page feed doesn't refetch page 2 and duplicate posts.
  const [page, setPage] = useState(() => Math.max(1, Math.ceil((posts?.results?.length ?? 0) / 10)));
  const [loadingMore, setLoadingMore] = useState(false);
  const pageLoading = false;
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const user = useAppSelector((state) => state.user as any);
  const herds = useAppSelector((state) => state.herds as any);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openAuthSheet = useAuthSheet();

  const allHerds = herds?.topHerds || [];

  // Filter herds based on search
  const filteredSubs = allHerds.filter((herd: any) =>
    herd.herdName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubClick = (herdName: string) => {
    navigate(`/herd/${herdName}`);
  };

  const handleRefresh = useCallback(async () => {
    try {
      await dispatch(refreshPosts());
      setPage(1);
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  }, [dispatch]);

  // Standard pull-down-to-refresh on the home feed (All tab).
  const { anchorRef, distance, refreshing } = usePullToRefresh(handleRefresh, {
    enabled: activeTab === 0,
  });

  // Keep the All-tab panel's scroll position across back navigation. Scoped
  // because the home entry has one scroller per tab panel.
  const restoreAnchor = useScrollRestoration('all');

  // Feed tabs live in the shared mobile top bar (part of the glass header).
  useMobileHeader(
    {
      type: 'tabs',
      tabs: [
        { label: 'All', value: 0 },
        { label: 'Herds', value: 1 },
        { label: 'Active', value: 2 },
      ],
      value: activeTab,
      onChange: value => setActiveTab(value as number),
    },
    [activeTab]
  );

  const handleLoadPosts = async () => {
    try {
      setLoadingMore(true);
      dispatch(loadMorePosts(page + 1));
      setPage((prevState) => prevState + 1);
      setLoadingMore(false);
    } catch (err) {
      setLoadingMore(false);
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  useEffect(() => trackScrollDepth('main_feed'), []);

  // Feed tabs are rendered in the shared mobile top bar (see useMobileHeader);
  // the panels below slide with the swipe gesture and scroll independently.
  const allPanel = (
    <Box
      key="all"
      ref={(el: HTMLDivElement | null) => {
        anchorRef.current = el;
        restoreAnchor.current = el;
      }}
      sx={{ pt: 2, pb: 'var(--space-stack-normal)' }}
    >
      {/* Pull-to-refresh indicator */}
          <Box
            sx={{
              height: distance,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: refreshing || distance === 0 ? 'height 0.2s ease' : 'none',
            }}
          >
            {/* The horn spiral winds with the pull, then spins while refreshing. */}
            <Box sx={{ opacity: distance > 0 ? 1 : 0 }}>
              <GoatMascot
                variant={refreshing ? 'spin' : 'static'}
                size={34}
                rotate={refreshing ? 0 : (distance / 70) * 360}
              />
            </Box>
          </Box>
          {posts?.results && !pageLoading ? (
            posts.results.map((post: any) => (
              <PostCard
                key={post.id}
                post={post}
                toggleUpvote={toggleUpvote}
                toggleDownvote={toggleDownvote}
                toggleReaction={toggleReaction}
                variant="expanded"
              />
            ))
          ) : (
            <LoadingSpinner text={'Fetching posts. Wait a sec.'} />
          )}

          {posts && posts.results && posts.results.length === 0 && !pageLoading && (
            <EmptyState
              title="The pasture is empty"
              subtitle="No posts yet — be the first to share something."
              sx={{ mt: 'var(--space-empty-state-offset)' }}
            />
          )}

      {posts && posts.results && !pageLoading && (
        <InfiniteScrollSentinel
          onLoadMore={handleLoadPosts}
          loading={loadingMore}
          hasMore={'next' in posts}
          totalCount={posts.totalCount as number | undefined}
          loadedCount={posts.results.length}
          renderSkeleton={(i) => <PostCardSkeleton key={i} variant="expanded" />}
        />
      )}
      {posts && posts.results && posts.results.length > 0 && !('next' in posts) && !pageLoading && <EndOfFeed />}
    </Box>
  );

  const herdsPanel = (
    <Box key="herds" sx={{ width: '100%', pt: 2 }}>
      <Box sx={{ p: 2, pb: 1 }}>
            <TextField
              fullWidth
              placeholder="Search herds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            />
          </Box>

          {allHerds && allHerds.length > 0 ? (
            <List sx={{ width: '100%' }}>
              {filteredSubs.map((herd: any) => (
                <ListItem
                  key={herd.id}
                  disablePadding
                  secondaryAction={
                    (
                      <Button
                        size="small"
                        startIcon={isJoined(herd.id, user) ? <CheckIcon /> : <AddIcon />}
                        variant={isJoined(herd.id, user) ? 'outlined' : 'contained'}
                        onClick={() => handleJoinHerd(herd.id, herd.herdName, dispatch, user, openAuthSheet)}
                        sx={{
                          mr: 1,
                          backgroundColor: isJoined(herd.id, user) ? 'transparent' : 'primary.main',
                          color: isJoined(herd.id, user) ? 'primary.main' : theme.tokens.colors.onPrimary,
                          borderColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: isJoined(herd.id, user) ? theme.tokens.colors.primarySubtle : 'primary.dark',
                          }
                        }}
                      >
                        {isJoined(herd.id, user) ? 'Joined' : 'Join'}
                      </Button>
                    )
                  }
                >
                  <ListItemButton onClick={() => handleSubClick(herd.herdName)}>
                    <ListItemText primary={`${herd.herdName}`} secondary={`${herd.memberCount ?? 0} members`} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <LoadingSpinner text="Loading herds..." />
          )}

          {filteredSubs.length === 0 && allHerds.length > 0 && (
            <EmptyState
              size={56}
              title="No herds found"
              subtitle="Try a different search."
            />
          )}
    </Box>
  );

  const activePanel = (
    <Box key="active" sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h6" color="text.secondary">
        TBD
      </Typography>
    </Box>
  );

  return (
    <SwipeTabViews index={activeTab} onChange={setActiveTab}>
      {[allPanel, herdsPanel, activePanel]}
    </SwipeTabViews>
  );
};

export default MobilePostListCompact;
